import type { ComponentType } from 'react'
import type { RouteObject } from 'react-router-dom'
import { importWithChunkReload } from '@/shared/lib/chunk-load-recovery'
import { createPermissionGuardedPage } from '@/shared/components/create-permission-guarded-page'
import { StockPermissions } from './permissions'

async function lazyGuardedRoute(
  importPage: () => Promise<Record<string, ComponentType>>,
  exportName: string,
  perm: string | readonly string[],
  options: {
    mode?: 'all' | 'any'
    administratorBypass?: boolean
  } = {},
): Promise<{ Component: ComponentType }> {
  const mode = options.mode ?? 'any'
  const mod = await importWithChunkReload(importPage)
  const Page = mod[exportName]
  if (!Page) {
    throw new Error(`Stocks route page export "${exportName}" not found`)
  }
  return {
    Component: createPermissionGuardedPage(perm, Page, {
      mode,
      ...(options.administratorBypass !== undefined
        ? { administratorBypass: options.administratorBypass }
        : {}),
    }),
  }
}

/** Admin entry + tenant-scoped stock routes. */
export const stockRoutes: RouteObject[] = [
  {
    path: 'material-request',
    lazy: async () => {
      const mod = await importWithChunkReload(() => import('./pages/material-request-entry-page'))
      return { Component: mod.MaterialRequestEntryPage }
    },
  },
  {
    path: 'admin/stocks',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/stocks-admin-redirect-page'),
        'StocksAdminRedirectPage',
        StockPermissions.workspaceAccess,
      ),
  },
  {
    path: 'admin/stock-approvals',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/stock-approvals-redirect-page'),
        'StockApprovalsRedirectPage',
        StockPermissions.adminApprovalsAccess,
      ),
  },
  {
    path: 't/:tenantId/stocks',
    lazy: async () => {
      const mod = await importWithChunkReload(() => import('./pages/stocks-layout-page'))
      return { Component: mod.StocksLayoutPage }
    },
    children: [
      {
        index: true,
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/stocks-dashboard-page'),
            'StocksDashboardPage',
            StockPermissions.dashboardAccess,
          ),
      },
      {
        path: 'approvals',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/stock-approvals-page'),
            'StockApprovalsPage',
            StockPermissions.adminApprovalsAccess,
          ),
      },
      {
        path: 'recipient-issue-report',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/stock-recipient-issue-report-page'),
            'StockRecipientIssueReportPage',
            StockPermissions.adminApprovalsAccess,
          ),
      },
      {
        path: 'inventory',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/inventory-page'),
            'InventoryPage',
            StockPermissions.dashboardAccess,
          ),
      },
      {
        path: 'my-requests',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/supervisor-material-request-page'),
            'SupervisorMaterialRequestPage',
            StockPermissions.selfRequest,
            { administratorBypass: false },
          ),
      },
      {
        path: 'requisitions',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/requisitions-board-page'),
            'RequisitionsBoardPage',
            StockPermissions.requisitionAccess,
          ),
      },
      {
        path: 'requisitions/new',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/create-requisition-page'),
            'CreateRequisitionPage',
            StockPermissions.approvalAdmin,
          ),
      },
      {
        path: 'requisitions/:requisitionId',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/requisition-detail-page'),
            'RequisitionDetailPage',
            StockPermissions.access,
          ),
      },
      {
        path: 'fulfilment',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/fulfilment-queue-page'),
            'FulfilmentQueuePage',
            StockPermissions.fulfilmentAccess,
          ),
      },
      {
        path: 'alerts',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/low-stock-alerts-page'),
            'LowStockAlertsPage',
            StockPermissions.alertsAccess,
          ),
      },
      {
        path: 'procurement',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/procurement-queue-page'),
            'ProcurementQueuePage',
            StockPermissions.procurementAccess,
          ),
      },
      {
        path: 'procurement/:caseId',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/procurement-case-page'),
            'ProcurementCasePage',
            StockPermissions.procurementAccess,
          ),
      },
      {
        path: 'billing-exceptions',
        lazy: () =>
          lazyGuardedRoute(
            () => import('./pages/billing-exceptions-page'),
            'BillingExceptionsPage',
            StockPermissions.billingAccess,
          ),
      },
    ],
  },
]
